<template>
    <fieldset
        class="flex flex-col gap-1"
        :aria-describedby="ariaDescribedBy"
        :aria-errormessage="hasError ? errorId : undefined"
    >
        <Label
            override-element="legend"
            :label="props.label"
            :input-id="inputId"
            :help-button-text="props.helpButtonText"
            :is-required="props.isRequired"
            :is-disabled="isGroupDisabled"
        />

        <p v-if="props.description" :id="descriptionId" class="text-sm text-gray-500">
            {{ props.description }}
        </p>

        <p v-if="props.disabledExplanation" :id="disabledExplanationId" class="text-sm text-gray-500 italic">
            {{ props.disabledExplanation }}
        </p>

        <div v-for="option in props.options" :key="option.id" class="flex flex-row items-center gap-2">
            <input
                :id="option.id"
                :value="option.value"
                :checked="option.isChecked"
                :aria-disabled="isOptionDisabled(option) ? true : undefined"
                :name="props.name"
                type="radio"
                class="size-4 aria-disabled:cursor-not-allowed aria-disabled:border-gray-300 aria-disabled:bg-gray-300 cursor-pointer focus:outline-hidden focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-sky-700 focus-visible:ring-offset-2 aria-disabled:focus:ring-gray-500"
                :class="[
                    hasError
                        ? 'border-red-400 text-red-700 focus:ring-red-700 focus:border-red-700'
                        : 'border-gray-300 text-sky-700 focus:ring-sky-700 focus:border-sky-700',
                ]"
                :required="isOptionDisabled(option) ? undefined : option.isRequired"
                :aria-invalid="hasError ? true : undefined"
                @click="handleClick($event, option)"
                @change="handleChange($event, option)"
                @blur="$emit('blur', $event)"
            />

            <label
                class="flex flex-row items-center text-sm font-normal"
                :class="[isOptionDisabled(option) ? 'text-gray-500' : 'text-gray-700']"
                :for="option.id"
            >
                <span class="text-nowrap">{{ option.label }}</span>

                <HelpButton v-if="option.helpButtonText" class="ml-1" :text="option.helpButtonText" />
            </label>
        </div>

        <div v-if="hasError" :id="errorId" role="alert" aria-live="polite" class="text-sm text-red-600">
            <p v-for="message in errorMessages" :key="message">
                {{ message }}
            </p>
        </div>
    </fieldset>
</template>

<script setup lang="ts">
import { computed, useId, watchEffect } from 'vue';
import HelpButton from '../HelpButton/HelpButton.vue';
import Label from './Label.vue';

type RadioButtonOption = {
    id: string;
    label: string;
    isChecked: boolean;
    value: string;
    isRequired?: boolean;
    isDisabled?: boolean;
    helpButtonText?: string;
};

type RadioButtonGroupProps = {
    label: string;
    name: string;
    options: RadioButtonOption[];
    description?: string;
    helpButtonText?: string;
    isRequired?: boolean;
    errorMessages?: string[];
    disabledExplanation?: string;
};

type RadioButtonGroupEmits = {
    (e: 'change', v: Event): void;
    (e: 'blur', v: Event): void;
};

const props = defineProps<RadioButtonGroupProps>();
const emit = defineEmits<RadioButtonGroupEmits>();

const id = useId();
const inputId = `input-${id}`;
const descriptionId = `description-${id}`;
const disabledExplanationId = `disabled-explanation-${id}`;
const errorId = `error-${id}`;

const hasError = computed(() => props.errorMessages && props.errorMessages.length > 0);

const isGroupDisabled = computed(() => Boolean(props.disabledExplanation));

const ariaDescribedBy = computed(() => {
    const ids = [
        props.description ? descriptionId : undefined,
        props.disabledExplanation ? disabledExplanationId : undefined,
    ];

    return ids.filter(Boolean).join(' ') ?? undefined;
});

function isOptionDisabled(option: RadioButtonOption): boolean {
    return isGroupDisabled.value || Boolean(option.isDisabled);
}

function handleClick(event: MouseEvent, option: RadioButtonOption) {
    if (isOptionDisabled(option)) {
        event.preventDefault();
    }
}

function handleChange(event: Event, option: RadioButtonOption) {
    if (!isOptionDisabled(option)) {
        emit('change', event);
    }
}

watchEffect(() => {
    if (props.options.filter((option) => option.isChecked).length > 1) {
        throw new Error('RadioButtonGroup: More than one option is marked as checked.');
    }
});
</script>
